import { DeviceType } from '$lib/constants';
import logger from '$lib/logger';
import { primoRTUSettings } from '$lib/PersistedState.svelte';
import { modbusDebugEnabled } from '$lib/State.svelte';
import { invoke } from '@tauri-apps/api/core';
import { BaseDevice } from '../base';

class RTUClientError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RTUClientError';
  }
}

/**
 * Primo roaster device using Delta DTB900 controller via Modbus RTU
 *
 * Register addresses (from Delta DTB900 documentation and manufacturer specs):
 * - 0x1000 (4096 decimal): Process Value (PV) - actual temperature reading in 0.1 units
 *   Note: While not explicitly mentioned in specs, this is the standard register for PV
 *   in Delta DTB900 controllers (typically one register before the setpoint)
 * - 0x1001 (4097 decimal): Set Point (SV) - temperature setpoint in 0.1 units (confirmed: 1001H)
 * - 0x1005 (4101 decimal): Control Method (confirmed: 1005H)
 * - 0x1012 (4114 decimal): Output #1 - gas valve control (read/write) (confirmed: 1012H)
 *
 * All addresses are in hex notation (e.g., 1001H = 0x1001 = 4097 decimal)
 * Temperature values are stored in 0.1 units (e.g., 250 = 25.0°C or °F)
 */
export class PrimoDevice extends BaseDevice {
  type: DeviceType = DeviceType.PRIMO;
  private portName: string = '';
  private slaveId: number = 1;
  private gasValveLevel: number | null = null;

  // Register addresses for Delta DTB900 controller
  // Using hex values directly as specified in documentation (1001H, 1005H, 1012H)
  private static readonly REGISTERS = {
    // Process Value (PV) - actual temperature reading in 0.1 units
    // Standard Delta DTB900 register pattern: PV is at 1000H (one before setpoint at 1001H)
    PROCESS_VALUE: 0x1000, // 4096 decimal (1000H)
    // Set Point (SV) - temperature setpoint in 0.1 units
    SET_POINT: 0x1001, // 4097 decimal (1001H) - confirmed by manufacturer
    // Control Method
    CONTROL_METHOD: 0x1005, // 4101 decimal (1005H) - confirmed by manufacturer
    // Output #1 - gas valve control (read/write)
    OUTPUT_1: 0x1012 // 4114 decimal (1012H) - confirmed by manufacturer
  };

  protected async onConnect(): Promise<boolean> {
    this.portName = primoRTUSettings.current.portName || '';
    this.slaveId = primoRTUSettings.current.slaveId;

    try {
      // Validate port name before attempting connection
      if (!this.portName || this.portName.trim() === '') {
        logger.error('Port name is not set. Cannot connect to Primo RTU device.');
        return false;
      }

      logger.info(`Attempting to connect to Primo RTU device on port: ${this.portName}`);

      // Define the registers we need to monitor for background reading
      const registersToMonitor = [
        PrimoDevice.REGISTERS.PROCESS_VALUE, // Temperature reading
        PrimoDevice.REGISTERS.OUTPUT_1 // Gas valve level
      ];

      const connectParams = {
        portPath: this.portName,
        baudRate: 9600, // Default baud rate, can be configured if needed
        slaveId: this.slaveId,
        registersToMonitor: registersToMonitor
      };

      logger.debug('Primo RTU connect parameters:', connectParams);

      // Connect to RTU device and start background monitoring
      await invoke('connect_rtu', connectParams);

      logger.info(
        `Connected to Primo RTU device on ${this.portName} with background monitoring for registers: ${registersToMonitor.join(', ')}`
      );
      return true;
    } catch (error) {
      logger.error(`Failed to connect to Primo RTU device: ${error}`);
      return false;
    }
  }

  protected onDisconnect(): void {
    invoke('disconnect_rtu', {
      portPath: this.portName,
      baudRate: 9600,
      slaveId: this.slaveId
    }).catch((error) => logger.warn(`Primo RTU disconnect error: ${error}`));
  }

  protected async updateReadings(): Promise<void> {
    try {
      // Read temperature from Process Value register
      const temperature = await this.readTemperatureRegister();

      // Read gas valve level from Output #1 register
      const gasValve = await this.readGasValveRegister();

      this.gasValveLevel = gasValve;

      if (modbusDebugEnabled.current === true) {
        logger.debug('DEVICE_PRIMO_READINGS', {
          temperature,
          gasValve,
          rawTemperatureRegister: PrimoDevice.REGISTERS.PROCESS_VALUE,
          rawGasValveRegister: PrimoDevice.REGISTERS.OUTPUT_1
        });
      }

      // Delta DTB900 returns temperature in 0.1 units, so divide by 10
      // Temperature is in the unit configured on the device (°C or °F)
      // We'll assume it's in °C, but this may need adjustment based on device configuration
      const temperatureCelsius = temperature / 10.0;

      this.localReadings = [
        temperatureCelsius, // Bean temp (TC1)
        null, // TC2: Not used
        null, // TC3: Not used
        null // TC4: Not used
      ];
    } catch (error) {
      if (error instanceof RTUClientError) {
        logger.error(`Primo RTU error: ${error.message}`);
      } else {
        logger.error(`Primo error: ${error}`);
      }
      throw error;
    }
  }

  protected async getPowerLevel(): Promise<number | null> {
    return this.gasValveLevel;
  }

  /**
   * Read temperature from Process Value register (0x1000 / 1000H)
   * Temperature is stored in 0.1 units (e.g., 250 = 25.0°C)
   *
   * Note: The Process Value register address (1000H) is inferred from standard
   * Delta DTB900 patterns. If this doesn't work, verify the actual PV register
   * address in the Delta DTB900 documentation table.
   */
  private async readTemperatureRegister(): Promise<number> {
    try {
      // Try to get cached value from background read loop
      const cachedValues = await this.getCachedRegisterValues([
        PrimoDevice.REGISTERS.PROCESS_VALUE
      ]);

      if (cachedValues.has(PrimoDevice.REGISTERS.PROCESS_VALUE)) {
        const value = cachedValues.get(PrimoDevice.REGISTERS.PROCESS_VALUE)!;
        if (modbusDebugEnabled.current === true) {
          logger.debug(
            `Using cached temperature value from register ${PrimoDevice.REGISTERS.PROCESS_VALUE}: ${value}`
          );
        }
        return value;
      }

      // Fallback to direct read
      if (modbusDebugEnabled.current === true) {
        logger.debug(
          `No cached value available for register ${PrimoDevice.REGISTERS.PROCESS_VALUE}, performing direct read`
        );
      }

      const results: number[] = await invoke('read_rtu', {
        portPath: this.portName,
        baudRate: 9600,
        slaveId: this.slaveId,
        reg: PrimoDevice.REGISTERS.PROCESS_VALUE,
        count: 1
      });

      if (results.length === 0) {
        throw new RTUClientError('No data returned from temperature register');
      }

      return results[0];
    } catch (error) {
      logger.error(
        `Failed to read temperature register ${PrimoDevice.REGISTERS.PROCESS_VALUE}: ${error}`
      );
      throw error;
    }
  }

  /**
   * Read gas valve level from Output #1 register (0x1012)
   */
  private async readGasValveRegister(): Promise<number> {
    try {
      // Try to get cached value from background read loop
      const cachedValues = await this.getCachedRegisterValues([PrimoDevice.REGISTERS.OUTPUT_1]);

      if (cachedValues.has(PrimoDevice.REGISTERS.OUTPUT_1)) {
        const value = cachedValues.get(PrimoDevice.REGISTERS.OUTPUT_1)!;
        if (modbusDebugEnabled.current === true) {
          logger.debug(
            `Using cached gas valve value from register ${PrimoDevice.REGISTERS.OUTPUT_1}: ${value}`
          );
        }
        return value;
      }

      // Fallback to direct read
      const results: number[] = await invoke('read_rtu', {
        portPath: this.portName,
        baudRate: 9600,
        slaveId: this.slaveId,
        reg: PrimoDevice.REGISTERS.OUTPUT_1,
        count: 1
      });

      if (results.length === 0) {
        throw new RTUClientError('No data returned from gas valve register');
      }

      return results[0];
    } catch (error) {
      logger.error(`Failed to read gas valve register ${PrimoDevice.REGISTERS.OUTPUT_1}: ${error}`);
      throw error;
    }
  }

  /**
   * Get cached register values from the background read loop
   */
  private async getCachedRegisterValues(registers: number[]): Promise<Map<number, number>> {
    try {
      const result = (await invoke('get_rtu_latest_values', {
        portPath: this.portName,
        baudRate: 9600,
        slaveId: this.slaveId,
        registers: registers
      })) as Record<number, number>;

      const cachedValues = new Map<number, number>();
      for (const [reg, value] of Object.entries(result)) {
        cachedValues.set(parseInt(reg), value);
      }

      return cachedValues;
    } catch (error) {
      logger.debug(`Failed to get cached register values: ${error}`);
      return new Map();
    }
  }
}

export default PrimoDevice;
